import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaSignOutAlt, FaCog } from 'react-icons/fa';

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isSettingsMenuOpen, setIsSettingsMenuOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const allNavItems = [
    {
      to: '/',
      label: 'Accueil',
      icon: 'bi-house',
      color: 'text-blue-600',
      roles: ['admin', 'user'] // Accessible à tous
    },
    {
      to: '/chatbot',
      label: 'Assistant IA',
      icon: 'bi-chat-dots',
      color: 'text-pink-600',
      roles: ['admin', 'user'] // Accessible à tous
    },
    {
      to: '/emails',
      label: 'Emails',
      icon: 'bi-envelope',
      color: 'text-green-600',
      roles: ['admin', 'user'] // Accessible à tous
    },
    {
      to: '/whatsapp',
      label: 'WhatsApp',
      icon: 'bi-chat',
      color: 'text-emerald-600',
      roles: ['admin', 'user']
    },
    {
      to: '/import',
      label: 'Import',
      icon: 'bi-upload',
      color: 'text-indigo-600',
      roles: ['admin', 'user'] // Accessible à tous
    },
  ];

  // Éléments admin regroupés dans un sous-menu Settings
  const adminSettingsItems = [
    {
      to: '/prompts',
      label: 'Prompts',
      icon: 'bi-lightbulb',
      color: 'text-yellow-600',
      roles: ['admin']
    },
    {
      to: '/register',
      label: 'Créer un compte',
      icon: 'bi-person-plus',
      color: 'text-green-600',
      roles: ['admin']
    },
    {
      to: '/drive',
      label: 'Fichiers importés',
      icon: 'bi-folder',
      color: 'text-orange-600',
      roles: ['admin']
    },
    {
      to: '/analytics',
      label: 'Analytics',
      icon: 'bi-bar-chart',
      color: 'text-purple-600',
      roles: ['admin']
    },
    {
      to: '/admin/whatsapp-monitoring',
      label: 'Monitoring WhatsApp',
      icon: 'bi-graph-up',
      color: 'text-teal-600',
      roles: ['admin']
    },
    {
      to: '/admin/settings',
      label: 'Paramètres Admin',
      icon: 'bi-gear-fill',
      color: 'text-gray-600',
      roles: ['admin']
    },
  ];

  // Filtrer les éléments de navigation selon le rôle de l'utilisateur
  const navItems = allNavItems.filter(item => {
    const userRole = user?.role?.toLowerCase() || 'user';
    return item.roles.includes(userRole);
  });

  // Vérifier si l'utilisateur est admin pour afficher le menu Settings
  const isAdmin = user?.role?.toLowerCase() === 'admin';
  const adminItems = adminSettingsItems.filter(item => item.roles.includes('admin'));

  const isActive = (path) => location.pathname === path;

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const toggleSettingsMenu = () => {
    setIsSettingsMenuOpen(!isSettingsMenuOpen);
  };

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
    navigate('/login');
  };

  if (!user) {
    return null;
  }

  return (
    <nav className="glass sticky top-0 z-50 border-b border-slate-200/50 bg-white/90 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo & Brand */}
          <div className="flex items-center space-x-3">
            <div className="relative">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                <i className="bi bi-envelope text-white text-lg"></i>
              </div>
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 rounded-full flex items-center justify-center">
                <i className="bi bi-sparkles text-yellow-700 text-xs"></i>
              </div>
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
              Discover
            </span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navItems.map((item) => {
              const active = isActive(item.to);

              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`nav-item relative group px-4 py-2 rounded-lg transition-all duration-200 ${
                    active
                      ? 'bg-white/50 shadow-sm'
                      : 'hover:bg-white/30'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <div className={`p-2 rounded-lg transition-colors ${
                      active
                        ? `bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg`
                        : `${item.color} group-hover:bg-slate-100`
                    }`}>
                      <i className={`${item.icon} text-sm`}></i>
                    </div>
                    <span className={`font-medium transition-colors ${
                      active ? 'text-slate-900' : 'text-slate-600 group-hover:text-slate-900'
                    }`}>
                      {item.label}
                    </span>
                  </div>
                </Link>
              );
            })}

            {/* Menu Settings pour les admins */}
            {isAdmin && adminItems.length > 0 && (
              <div className="relative">
                <button
                  onClick={toggleSettingsMenu}
                  className={`nav-item relative group px-4 py-2 rounded-lg transition-all duration-200 ${
                    adminItems.some(item => isActive(item.to))
                      ? 'bg-white/50 shadow-sm'
                      : 'hover:bg-white/30'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <div className={`p-2 rounded-lg transition-colors ${
                      adminItems.some(item => isActive(item.to))
                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg'
                        : 'text-slate-600 group-hover:bg-slate-100'
                    }`}>
                      <i className="bi bi-gear text-sm"></i>
                    </div>
                    <span className={`font-medium transition-colors ${
                      adminItems.some(item => isActive(item.to)) ? 'text-slate-900' : 'text-slate-600 group-hover:text-slate-900'
                    }`}>
                      Settings
                    </span>
                    <i className="bi bi-chevron-down text-slate-500"></i>
                  </div>
                </button>

                {isSettingsMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-slate-200 py-2 z-50">
                    {adminItems.map((item) => {
                      const active = isActive(item.to);

                      return (
                        <Link
                          key={item.to}
                          to={item.to}
                          onClick={() => setIsSettingsMenuOpen(false)}
                          className={`flex items-center space-x-3 px-4 py-2 text-sm transition-colors ${
                            active
                              ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-500'
                              : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                          }`}
                        >
                          <i className={`${item.icon} text-base`}></i>
                          <span className="font-medium">{item.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* User Menu */}
          <div className="hidden md:flex items-center space-x-4">
            <div className="relative">
              <button
                onClick={toggleUserMenu}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-white/30 transition-colors"
              >
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  <i className="bi bi-person text-white text-sm"></i>
                </div>
                <span className="text-sm font-medium text-slate-700">
                  {user?.full_name || user?.username || user?.email?.split('@')[0] || 'Utilisateur'}
                </span>
                <i className="bi bi-chevron-down text-slate-500"></i>
              </button>

              {isUserMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-2 z-50">
                  <div className="px-4 py-2 border-b border-slate-100">
                    <p className="text-sm font-medium text-slate-900">{user?.full_name || user?.username || user?.email?.split('@')[0]}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <i className="bi bi-box-arrow-right mr-2"></i>
                    Se déconnecter
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={toggleMobileMenu}
              className="p-2 rounded-lg text-slate-600 hover:text-slate-900 hover:bg-white/20 transition-colors"
            >
              {isMobileMenuOpen ? (
                <i className="bi bi-x text-xl"></i>
              ) : (
                <i className="bi bi-list text-xl"></i>
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200/50 bg-white/95 backdrop-blur-md relative z-50">
            <div className="px-4 py-4 space-y-2">
              {/* Navigation principale */}
              {navItems.map((item) => {
                const active = isActive(item.to);

                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                      active
                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                    }`}
                  >
                    <i className={`${item.icon} text-lg`}></i>
                    <span className="font-medium">{item.label}</span>
                  </Link>
                );
              })}

              {/* Section Settings pour les admins */}
              {isAdmin && adminItems.length > 0 && (
                <div className="border-t border-slate-200 pt-4 mt-4">
                  <div className="px-4 py-2 mb-2">
                    <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Administration</h3>
                  </div>
                  {adminItems.map((item) => {
                    const active = isActive(item.to);

                    return (
                      <Link
                        key={item.to}
                        to={item.to}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                          active
                            ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg'
                            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                        }`}
                      >
                        <i className={`${item.icon} text-lg`}></i>
                        <span className="font-medium">{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              )}

              {/* Mobile user info and logout */}
              <div className="border-t border-slate-200 pt-4 mt-4">
                <div className="px-4 py-2 mb-2">
                  <p className="text-sm font-medium text-slate-900">{user?.full_name || user?.username || user?.email?.split('@')[0]}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </div>
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center space-x-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                >
                  <i className="bi bi-box-arrow-right text-lg"></i>
                  <span className="font-medium">Se déconnecter</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* User menu overlay */}
      {isUserMenuOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsUserMenuOpen(false)}
        />
      )}

      {/* Settings menu overlay */}
      {isSettingsMenuOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsSettingsMenuOpen(false)}
        />
      )}
    </nav>
  );
};

export default Navbar; 